import {
  REMOVAL_BEYOND_SCOPE,
  computeZakopanyM2FromWidth,
  getExcavationSurfaceBucketName,
  resolveExcavationSurfaceBucket,
  type SpecialRemovalRow,
} from '../constants/excavation-material.constants';
import {
  isDeepeningWorkReportActivityName,
  workReportDeepeningCm,
} from './work-report-deepening-display.util';
import {
  formatUkonceniVRdLabel,
} from '../constants/object-workflow/ukonceni-v-rd.constants';

export const ZAFUK_SEKUNDARNI_OK_LIST_ITEM_CODE = 'pripojovany-zafuk-sekundarni-ok';
export const ZAFUK_SEKUNDARNI_OK_ACTIVITY_NAME = 'Záfuk sekundárních OK';

export function isZafukSekundarnichOkWorkReportRow(input: {
  activityName?: string | null;
  metadata?: Record<string, unknown> | null | undefined;
}): boolean {
  const code =
    typeof input.metadata?.['listItemCode'] === 'string'
      ? input.metadata['listItemCode'].trim()
      : '';
  if (code === ZAFUK_SEKUNDARNI_OK_LIST_ITEM_CODE) return true;
  return (input.activityName ?? '').trim() === ZAFUK_SEKUNDARNI_OK_ACTIVITY_NAME;
}

const POKLADKA_HDPE_MULTIDUCT_ACTIVITY_BASE = 'Pokládka HDPE, multiduct';

export interface WorkReportLineItemDetailInput {
  activityName?: string;
  excavationLengthM?: unknown;
  excavationMaterial?: string;
  excavationMaterialSubtype?: string;
  metadata?: Record<string, unknown> | null;
}

function parsePositiveNumber(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function formatDisplayNumber(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

function formatLengthM(n: number): string {
  return `${formatDisplayNumber(n)} m`;
}

function formatWxL(widthM: number, lengthM: number): string {
  return `${formatDisplayNumber(widthM)}×${formatDisplayNumber(lengthM)} m`;
}

function formatWxLxH(widthM: number, lengthM: number, depthM: number): string {
  return `${formatDisplayNumber(widthM)}×${formatDisplayNumber(lengthM)}×${formatDisplayNumber(depthM)} m`;
}

/** Položka ceníku „Pokládka HDPE, multiduct“ (včetně suffixu „– 12 m“ v `activityName`). */
export function isPokladkaHdpeMultiductWorkReportActivityName(name: string): boolean {
  const raw = (name ?? '').trim();
  if (!raw) return false;
  const base = raw.replace(/\s*[–-]\s*\d+(\.\d+)?\s*m\s*$/i, '').trim();
  return base === POKLADKA_HDPE_MULTIDUCT_ACTIVITY_BASE;
}

export function isBouraniWorkReportActivityName(name: string): boolean {
  return /bourání betonových základů/i.test(name ?? '');
}

export function isRozvozWorkReportActivityName(name: string): boolean {
  return /rozvoz/i.test(name ?? '');
}

/** Obnova povrchu / Obnova nad rámec (ceník unit m² / m³). */
export function isObnovaWorkReportActivityName(name: string): boolean {
  return /obnova/i.test(name ?? '');
}

export function isObnovaSurfaceWorkReportActivityName(name: string): boolean {
  return /^Obnova(?!\s+(?:nad\s+rámec|betonu))/i.test((name ?? '').trim());
}

export function isExcavationSurfaceWorkReportActivityName(name: string): boolean {
  return /^Výkop/i.test((name ?? '').trim());
}

export function isOstraneniNadRamecWorkReportActivityName(name: string): boolean {
  return /^(?:Ostranění|Odstranění)\s+nad\s+rámec/i.test((name ?? '').trim());
}

function excavationSnapshot(meta: Record<string, unknown> | null | undefined): Record<string, unknown> | undefined {
  const snap = meta?.['excavationSnapshot'];
  if (snap && typeof snap === 'object' && !Array.isArray(snap)) {
    return snap as Record<string, unknown>;
  }
  return undefined;
}

function sumSurfacesLength(snap: Record<string, unknown>): number | undefined {
  const raw = snap['excavationSurfaces'];
  if (!Array.isArray(raw)) return undefined;
  let sum = 0;
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const lm = parsePositiveNumber((item as Record<string, unknown>)['lengthM']);
    if (lm != null) sum += lm;
  }
  return sum > 0 ? sum : undefined;
}

function sumDeepeningSurfaceLength(snap: Record<string, unknown>, legacyCm?: unknown): number | undefined {
  const raw = snap['excavationSurfaces'];
  if (Array.isArray(raw)) {
    let sum = 0;
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const rec = item as Record<string, unknown>;
      const lm = parsePositiveNumber(rec['lengthM']);
      const cm = parsePositiveNumber(rec['deepeningCm']);
      if (lm != null && cm != null) sum += lm;
    }
    if (sum > 0) return sum;
  }
  const leg = parsePositiveNumber(legacyCm);
  if (leg != null) return sumSurfacesLength(snap) ?? parsePositiveNumber(snap['excavationLengthM']);
  return undefined;
}

/** Délka použitého HDPE/multiduct materiálu (m) — billing quantity ze SAP. */
export function resolveWorkReportPokladkaHdpeLengthM(input: WorkReportLineItemDetailInput): number | undefined {
  const meta = input.metadata;
  if (meta && typeof meta === 'object') {
    const fromMeta = parsePositiveNumber(meta['pokladkaHdpeLengthM']);
    if (fromMeta != null) return fromMeta;
  }
  const name = (input.activityName ?? '').trim();
  const suffix = name.match(/\s*[–-]\s*(\d+(?:[.,]\d+)?)\s*m\s*$/i);
  if (suffix?.[1]) {
    const parsed = parsePositiveNumber(suffix[1]);
    if (parsed != null) return parsed;
  }
  return undefined;
}

/** Délka trati (m) pro detail řádku — sloupec řádku, snapshot, součet povrchů. */
export function resolveWorkReportRouteLengthM(input: WorkReportLineItemDetailInput): number | undefined {
  const fromRow = parsePositiveNumber(input.excavationLengthM);
  if (fromRow != null) return fromRow;

  const meta = input.metadata;
  if (!meta || typeof meta !== 'object') return undefined;
  const snap = excavationSnapshot(meta);
  if (snap) {
    const fromSnap = parsePositiveNumber(snap['excavationLengthM']);
    if (fromSnap != null) return fromSnap;
    const fromSurfaces = sumSurfacesLength(snap);
    if (fromSurfaces != null) return fromSurfaces;
  }

  return (
    parsePositiveNumber(meta['removalBeyondScopeLengthM']) ??
    parsePositiveNumber(meta['excavationLengthM'])
  );
}

function asSpecialRemovalRow(raw: unknown): SpecialRemovalRow | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const code = typeof o['code'] === 'string' ? o['code'].trim() : '';
  const lengthM = parsePositiveNumber(o['lengthM']);
  if (!code || lengthM == null) return null;
  const widthM = parsePositiveNumber(o['widthM']) ?? 0;
  const depthM = parsePositiveNumber(o['depthM']);
  return { code, lengthM, widthM, ...(depthM != null ? { depthM } : {}) };
}

function findSpecialRemovalRow(
  input: WorkReportLineItemDetailInput,
  code: string,
): SpecialRemovalRow | null {
  const meta = input.metadata;
  const snap = meta ? excavationSnapshot(meta) : undefined;
  const rowsRaw = snap?.['specialRemovalRows'] ?? meta?.['specialRemovalRows'];
  if (Array.isArray(rowsRaw)) {
    for (const raw of rowsRaw) {
      const row = asSpecialRemovalRow(raw);
      if (row?.code === code) return row;
    }
  }
  const legacyCode =
    typeof meta?.['removalBeyondScope'] === 'string' ? meta['removalBeyondScope'].trim() : '';
  if (legacyCode === code) {
    const widthM = parsePositiveNumber(meta?.['removalBeyondScopeWidthM']) ?? 0;
    const lengthM =
      parsePositiveNumber(meta?.['removalBeyondScopeLengthM']) ??
      parsePositiveNumber(input.excavationLengthM);
    const depthM = parsePositiveNumber(meta?.['removalBeyondScopeDepthM']);
    if (lengthM != null) {
      return { code, lengthM, widthM, ...(depthM != null ? { depthM } : {}) };
    }
  }
  return null;
}

function formatMultiductDetail(input: WorkReportLineItemDetailInput): string | undefined {
  const len = resolveWorkReportPokladkaHdpeLengthM(input);
  return len != null ? `Délka materiálu: ${formatLengthM(len)}` : undefined;
}

function formatDeepeningDetail(input: WorkReportLineItemDetailInput): string | undefined {
  const snap = input.metadata ? excavationSnapshot(input.metadata) : undefined;
  const surfaces = snap?.['excavationSurfaces'];
  const legacy = snap?.['excavationDeepeningCm'];
  const cm = workReportDeepeningCm(surfaces, legacy);
  const len =
    (snap ? sumDeepeningSurfaceLength(snap, legacy) : undefined) ??
    resolveWorkReportRouteLengthM(input);
  const parts: string[] = [];
  if (len != null) parts.push(`Délka: ${formatLengthM(len)}`);
  if (cm != null) parts.push(`Hloubka prohloubení: ${cm} cm`);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

interface ResolvedVolumeOrSurfaceDims {
  lengthM?: number;
  widthM?: number;
  depthM?: number;
  quantity?: number;
  surfaceBucket?: string;
  hasMultipleSurfaces?: boolean;
}

function resolveVolumeOrSurfaceRowDimensions(
  input: WorkReportLineItemDetailInput,
  expectedUnit: 'm2' | 'm3',
): ResolvedVolumeOrSurfaceDims {
  const meta = input.metadata && typeof input.metadata === 'object' ? input.metadata : undefined;
  const snap = meta ? excavationSnapshot(meta) : undefined;
  const quantity = parsePositiveNumber(meta?.['quantity']);
  let lengthM = parsePositiveNumber(meta?.['lengthM']);
  let widthM = parsePositiveNumber(meta?.['widthM']);
  let depthM = parsePositiveNumber(meta?.['depthM']);
  let surfaceBucket =
    typeof meta?.['surfaceBucket'] === 'string' && meta['surfaceBucket'].trim()
      ? meta['surfaceBucket'].trim()
      : undefined;

  const excSurfaces = Array.isArray(snap?.['excavationSurfaces'])
    ? (snap!['excavationSurfaces'] as Array<Record<string, unknown>>)
    : [];
  const obnovaSurfaces = Array.isArray(snap?.['obnovaSurfaces'])
    ? (snap!['obnovaSurfaces'] as Array<Record<string, unknown>>)
    : [];
  const zakSurfaces = Array.isArray(snap?.['zakopanySurfaces'])
    ? (snap!['zakopanySurfaces'] as Array<Record<string, unknown>>)
    : Array.isArray(meta?.['zakopanySurfaces'])
      ? (meta!['zakopanySurfaces'] as Array<Record<string, unknown>>)
      : [];

  if (!surfaceBucket && input.excavationMaterial) {
    surfaceBucket = resolveExcavationSurfaceBucket(
      input.excavationMaterial,
      input.excavationMaterialSubtype,
    );
  }

  if (expectedUnit === 'm3') {
    if (lengthM == null || widthM == null || depthM == null) {
      const matchSeg =
        excSurfaces.find((s) => {
          const b =
            String(s['surfaceBucket'] ?? '').trim() ||
            resolveExcavationSurfaceBucket(
              String(s['excavationMaterial'] ?? ''),
              String(s['excavationMaterialSubtype'] ?? ''),
            );
          if (surfaceBucket && b === surfaceBucket) return true;
          if (
            input.excavationMaterial &&
            String(s['excavationMaterial'] ?? '').trim() === input.excavationMaterial.trim()
          ) {
            return true;
          }
          return false;
        }) ?? (excSurfaces.length === 1 ? excSurfaces[0] : undefined);

      if (matchSeg) {
        lengthM =
          lengthM ??
          parsePositiveNumber(matchSeg['volumeLengthM']) ??
          parsePositiveNumber(matchSeg['lengthM']);
        widthM =
          widthM ??
          parsePositiveNumber(matchSeg['volumeWidthM']) ??
          parsePositiveNumber(matchSeg['widthM']);
        depthM = depthM ?? parsePositiveNumber(matchSeg['depthM']);
        if (!surfaceBucket) {
          surfaceBucket =
            String(matchSeg['surfaceBucket'] ?? '').trim() ||
            resolveExcavationSurfaceBucket(
              String(matchSeg['excavationMaterial'] ?? ''),
              String(matchSeg['excavationMaterialSubtype'] ?? ''),
            );
        }
      } else if (excSurfaces.length > 1) {
        // Legacy souhrnný řádek bez rozdělení po površích: pokud mají všechny úseky stejnou šířku a hloubku, zobrazíme součet délek + šířku + hloubku
        const firstW =
          parsePositiveNumber(excSurfaces[0]?.['volumeWidthM']) ??
          parsePositiveNumber(excSurfaces[0]?.['widthM']);
        const firstD = parsePositiveNumber(excSurfaces[0]?.['depthM']);
        const sumLen = excSurfaces.reduce(
          (acc, s) =>
            acc +
            (parsePositiveNumber(s['volumeLengthM']) ?? parsePositiveNumber(s['lengthM']) ?? 0),
          0,
        );
        if (sumLen > 0) lengthM = lengthM ?? sumLen;
        if (firstW != null) widthM = widthM ?? firstW;
        if (firstD != null) depthM = depthM ?? firstD;
      }
    }
  } else {
    // expectedUnit === 'm2'
    if (lengthM == null || widthM == null) {
      const isObnova = isObnovaWorkReportActivityName(input.activityName ?? '');
      if (isObnova && obnovaSurfaces.length > 0) {
        const matchOb =
          obnovaSurfaces.find(
            (s) => !surfaceBucket || String(s['surfaceBucket'] ?? '').trim() === surfaceBucket,
          ) ?? obnovaSurfaces[0];
        if (matchOb) {
          lengthM = lengthM ?? parsePositiveNumber(matchOb['lengthM']);
          widthM = widthM ?? parsePositiveNumber(matchOb['widthM']);
        }
      }
      if ((lengthM == null || widthM == null) && zakSurfaces.length > 0) {
        const matchZak = zakSurfaces[0];
        if (matchZak) {
          lengthM = lengthM ?? parsePositiveNumber(matchZak['lengthM']);
          widthM = widthM ?? parsePositiveNumber(matchZak['widthM']);
        }
      }
      if ((lengthM == null || widthM == null) && excSurfaces.length > 0) {
        const matchSeg =
          excSurfaces.find((s) => {
            const b =
              String(s['surfaceBucket'] ?? '').trim() ||
              resolveExcavationSurfaceBucket(
                String(s['excavationMaterial'] ?? ''),
                String(s['excavationMaterialSubtype'] ?? ''),
              );
            if (surfaceBucket && b === surfaceBucket) return true;
            if (
              input.excavationMaterial &&
              String(s['excavationMaterial'] ?? '').trim() === input.excavationMaterial.trim() &&
              String(s['excavationMaterial'] ?? '').trim() !== 'VOLNY_TEREN'
            ) {
              return true;
            }
            return b !== 'VOLNY_TEREN';
          }) ?? excSurfaces[0];
        if (matchSeg) {
          lengthM =
            lengthM ??
            parsePositiveNumber(matchSeg['surfaceLengthM']) ??
            parsePositiveNumber(matchSeg['lengthM']);
          widthM =
            widthM ??
            parsePositiveNumber(matchSeg['surfaceWidthM']) ??
            parsePositiveNumber(matchSeg['widthM']);
        }
      }
    }
  }

  return {
    lengthM,
    widthM,
    depthM,
    quantity,
    surfaceBucket,
    hasMultipleSurfaces: excSurfaces.length > 1,
  };
}

function formatM3DimensionsDetail(dims: ResolvedVolumeOrSurfaceDims): string | undefined {
  const parts: string[] = [];
  if (
    dims.surfaceBucket &&
    (dims.surfaceBucket !== 'VOLNY_TEREN' || dims.hasMultipleSurfaces)
  ) {
    parts.push(`Povrch: ${getExcavationSurfaceBucketName(dims.surfaceBucket)}`);
  }
  if (dims.lengthM != null) parts.push(`Délka: ${formatLengthM(dims.lengthM)}`);
  if (dims.widthM != null) parts.push(`Šířka: ${formatLengthM(dims.widthM)}`);
  if (dims.depthM != null) parts.push(`Hloubka: ${formatLengthM(dims.depthM)}`);
  if (dims.quantity != null) parts.push(`Objem: ${formatDisplayNumber(dims.quantity)} m³`);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function formatM2DimensionsDetail(
  dims: ResolvedVolumeOrSurfaceDims,
  fallbackM2?: number,
): string | undefined {
  const m2 = dims.quantity ?? fallbackM2;
  const parts: string[] = [];
  if (dims.lengthM != null && dims.widthM != null) {
    parts.push(`Délka: ${formatLengthM(dims.lengthM)}`);
    parts.push(`Šířka: ${formatLengthM(dims.widthM)}`);
  }
  if (m2 != null) {
    parts.push(`Plocha: ${formatDisplayNumber(m2)} m²`);
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function formatSurfaceDetail(input: WorkReportLineItemDetailInput): string | undefined {
  const meta = input.metadata && typeof input.metadata === 'object' ? input.metadata : undefined;
  const unit = typeof meta?.['unit'] === 'string' ? meta['unit'].trim().toLowerCase() : '';
  const listItemCode =
    typeof meta?.['listItemCode'] === 'string' ? meta['listItemCode'].trim() : '';

  if (unit === 'm3' || listItemCode.startsWith('vykop-m3-')) {
    const dims = resolveVolumeOrSurfaceRowDimensions(input, 'm3');
    const formatted = formatM3DimensionsDetail(dims);
    if (formatted) return formatted;
  }
  if (unit === 'm2' || listItemCode.startsWith('vykop-m2-')) {
    const dims = resolveVolumeOrSurfaceRowDimensions(input, 'm2');
    const formatted = formatM2DimensionsDetail(dims);
    if (formatted) return formatted;
  }

  const len = resolveWorkReportRouteLengthM(input);
  return len != null ? `Délka povrchu: ${formatLengthM(len)}` : undefined;
}

function formatBouraniDetail(input: WorkReportLineItemDetailInput): string | undefined {
  const row = findSpecialRemovalRow(input, REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY);
  if (!row) return undefined;
  const widthM = parsePositiveNumber(row.widthM);
  const lengthM = parsePositiveNumber(row.lengthM);
  const depthM = parsePositiveNumber(row.depthM);
  if (widthM != null && lengthM != null && depthM != null) {
    return formatWxLxH(widthM, lengthM, depthM);
  }
  return undefined;
}

function formatRozvozDetail(input: WorkReportLineItemDetailInput): string | undefined {
  const row = findSpecialRemovalRow(input, REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA);
  if (!row) return undefined;
  const widthM = parsePositiveNumber(row.widthM);
  const lengthM = parsePositiveNumber(row.lengthM);
  const depthM = parsePositiveNumber(row.depthM);
  if (widthM != null && lengthM != null && depthM != null) {
    return formatWxLxH(widthM, lengthM, depthM);
  }
  if (widthM != null && lengthM != null) {
    return formatWxL(widthM, lengthM);
  }
  return undefined;
}

/**
 * Plocha Obnovy (m²) — `metadata.quantity` (uložené při fakturaci), legacy skaláře
 * `restorationZakopanyM2` / `workBeyondScopeZakopanyM2`, nebo výpočet šířka × délka.
 */
export function resolveWorkReportObnovaM2(input: WorkReportLineItemDetailInput): number | undefined {
  const meta = input.metadata;
  if (meta && typeof meta === 'object') {
    const unit =
      typeof meta['unit'] === 'string' ? meta['unit'].trim().toLowerCase() : '';
    // Obnova volný terén (m³) musí zůstat v volume větvi — ne jako plocha.
    if (unit === 'm3' || unit === 'm³') return undefined;
    const listItemCode =
      typeof meta['listItemCode'] === 'string' ? meta['listItemCode'].trim() : '';
    if (listItemCode === 'obnova-m3-volny-teren') return undefined;
    if (/obnova\s+volný\s+terén/i.test(input.activityName ?? '')) return undefined;
    const fromQty = parsePositiveNumber(meta['quantity']);
    if (fromQty != null && (unit === 'm2' || unit === '' || unit === 'm²')) {
      return fromQty;
    }
  }

  const snap = meta ? excavationSnapshot(meta) : undefined;
  const sources: Array<Record<string, unknown>> = [];
  if (meta && typeof meta === 'object') sources.push(meta);
  if (snap) sources.push(snap);

  for (const src of sources) {
    for (const key of ['restorationZakopanyM2', 'workBeyondScopeZakopanyM2'] as const) {
      const v = parsePositiveNumber(src[key]);
      if (v != null) return v;
    }
  }

  for (const src of sources) {
    const zs = src['zakopanySurfaces'];
    if (!Array.isArray(zs) || zs.length === 0) continue;
    let sum = 0;
    for (const raw of zs) {
      if (!raw || typeof raw !== 'object') continue;
      const rec = raw as Record<string, unknown>;
      const direct = parsePositiveNumber(rec['m2']);
      if (direct != null) {
        sum += direct;
        continue;
      }
      const widthM = parsePositiveNumber(rec['widthM']);
      const lengthM = parsePositiveNumber(rec['lengthM']);
      if (widthM != null && lengthM != null) {
        const m2 = computeZakopanyM2FromWidth(widthM, lengthM);
        if (m2 > 0) sum += m2;
      }
    }
    if (sum > 0) return Math.round(sum * 100) / 100;
  }

  for (const src of sources) {
    const widthM =
      parsePositiveNumber(src['restorationZakopanyWidthM']) ??
      parsePositiveNumber(src['workBeyondScopeZakopanyWidthM']);
    const lengthM =
      parsePositiveNumber(src['zakopanyLengthM']) ??
      parsePositiveNumber(src['excavationLengthM']) ??
      parsePositiveNumber(input.excavationLengthM);
    if (widthM != null && lengthM != null) {
      const m2 = computeZakopanyM2FromWidth(widthM, lengthM);
      if (m2 > 0) return m2;
    }
  }

  if (meta && typeof meta === 'object') {
    const fromQty = parsePositiveNumber(meta['quantity']);
    if (fromQty != null) return fromQty;
  }
  return undefined;
}

function formatObnovaDetail(input: WorkReportLineItemDetailInput): string | undefined {
  const meta = input.metadata && typeof input.metadata === 'object' ? input.metadata : undefined;
  const unit = typeof meta?.['unit'] === 'string' ? meta['unit'].trim().toLowerCase() : '';
  const listItemCode =
    typeof meta?.['listItemCode'] === 'string' ? meta['listItemCode'].trim() : '';

  if (
    unit === 'm3' ||
    listItemCode === 'obnova-m3-volny-teren' ||
    /obnova\s+volný\s+terén/i.test(input.activityName ?? '')
  ) {
    const dims = resolveVolumeOrSurfaceRowDimensions(input, 'm3');
    const formatted = formatM3DimensionsDetail(dims);
    if (formatted) return formatted;
  }

  const dims = resolveVolumeOrSurfaceRowDimensions(input, 'm2');
  const m2 = resolveWorkReportObnovaM2(input);
  return formatM2DimensionsDetail(dims, m2);
}

function formatOstraneniNadRamecDetail(input: WorkReportLineItemDetailInput): string | undefined {
  const dims = resolveVolumeOrSurfaceRowDimensions(input, 'm2');
  if (dims.lengthM == null && input.excavationLengthM != null) {
    dims.lengthM = parsePositiveNumber(input.excavationLengthM);
  }
  return formatM2DimensionsDetail(dims);
}

function formatZafukSekundarnichOkDetail(input: WorkReportLineItemDetailInput): string | undefined {
  const meta = input.metadata;
  if (!meta || typeof meta !== 'object') return undefined;
  const parts: string[] = [];
  const ukLabel = formatUkonceniVRdLabel(
    typeof meta['ukonceniVRd'] === 'string' ? meta['ukonceniVRd'] : null,
    typeof meta['ukonceniVRdOther'] === 'string' ? meta['ukonceniVRdOther'] : null,
  );
  if (ukLabel) parts.push(`Ukončení v RD: ${ukLabel}`);
  const rozName =
    typeof meta['pripojovanyZafouknutoZRozvadecName'] === 'string'
      ? meta['pripojovanyZafouknutoZRozvadecName'].trim()
      : '';
  if (rozName) parts.push(`Z rozvaděče: ${rozName}`);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/**
 * Text pod řádkem výkazu — pouze údaje relevantní k typu položky
 * (výkop, prohloubení, multiduct, bourání, rozvoz, obnova).
 * Bez surových klíčů metadat a bez redundantního výpisu celého snapshotu.
 */
export function formatWorkReportLineItemDetail(input: WorkReportLineItemDetailInput): string | undefined {
  const name = (input.activityName ?? '').trim();
  if (!name) return undefined;

  if (isPokladkaHdpeMultiductWorkReportActivityName(name)) {
    return formatMultiductDetail(input);
  }
  if (isDeepeningWorkReportActivityName(name)) {
    return formatDeepeningDetail(input);
  }
  if (isBouraniWorkReportActivityName(name)) {
    return formatBouraniDetail(input);
  }
  if (isRozvozWorkReportActivityName(name)) {
    return formatRozvozDetail(input);
  }
  if (isObnovaWorkReportActivityName(name)) {
    return formatObnovaDetail(input);
  }
  if (isOstraneniNadRamecWorkReportActivityName(name)) {
    return formatOstraneniNadRamecDetail(input);
  }
  if (isExcavationSurfaceWorkReportActivityName(name)) {
    return formatSurfaceDetail(input);
  }
  if (isZafukSekundarnichOkWorkReportRow(input)) {
    return formatZafukSekundarnichOkDetail(input);
  }
  return undefined;
}
